import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Rocket, Building2, ShoppingCart, Wand2, Calendar,
  Check, ChevronRight, ChevronLeft, Calculator, Send,
  Loader2, CheckCircle, ArrowRight, Sparkles, Star, Users
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { quoteConfig, getQuoteText, calculateQuotePrice } from '@/config/quoteConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const icons = { FileText, Rocket, Building2, ShoppingCart, Wand2, Calendar, Users };

// Animated number component for price changes
const AnimatedPrice = ({ value, prefix = '€' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  
  useEffect(() => {
    const duration = 300;
    const steps = 10;
    const stepDuration = duration / steps;
    const diff = value - displayValue;
    const increment = diff / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(prev => Math.round(prev + increment));
      }
    }, stepDuration);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return <span>{prefix}{displayValue.toLocaleString('nl-NL')}</span>;
};

// Confetti effect for success
const Confetti = () => {
  const colors = ['#2563EB', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            y: -10, 
            x: Math.random() * 300, 
            rotate: 0,
            opacity: 1 
          }}
          animate={{ 
            y: 400, 
            x: Math.random() * 300,
            rotate: Math.random() * 360,
            opacity: 0
          }}
          transition={{ 
            duration: 2 + Math.random(), 
            delay: Math.random() * 0.5,
            ease: "easeOut"
          }}
          className="absolute w-2 h-2 rounded-full"
          style={{ backgroundColor: colors[i % colors.length] }}
        />
      ))}
    </div>
  );
};

const translations = {
  nl: {
    title: 'Offerte Calculator',
    subtitle: 'Bereken je prijs in 6 stappen',
    step: 'Stap',
    of: 'van',
    next: 'Volgende',
    back: 'Terug',
    calculate: 'Bereken prijs',
    steps: [
      'Type website',
      'Extra pagina\'s',
      'Design',
      'Functionaliteiten',
      'Hosting',
      'Tijdlijn'
    ],
    selectFeatures: 'Selecteer extra functies (optioneel)',
    includedFree: 'Gratis inbegrepen',
    summary: 'Jouw offerte',
    oneTimePrice: 'Eenmalige kosten',
    yearlyHosting: 'Hosting (per jaar)',
    totalFirstYear: 'Totaal eerste jaar',
    requestQuote: 'Offerte aanvragen',
    name: 'Naam',
    email: 'E-mailadres',
    phone: 'Telefoonnummer',
    company: 'Bedrijfsnaam',
    message: 'Extra opmerkingen',
    sending: 'Versturen...',
    success: 'Bedankt! We nemen binnen 24 uur contact met je op.',
    error: 'Er ging iets mis. Probeer het opnieuw.',
    close: 'Sluiten',
    startOver: 'Opnieuw beginnen',
    monthly: 'per maand',
    yearly: 'per jaar',
    surcharge: 'spoedtoeslag',
    quotesRequested: 'offertes aangevraagd'
  },
  en: {
    title: 'Quote Calculator',
    subtitle: 'Calculate your price in 6 steps',
    step: 'Step',
    of: 'of',
    next: 'Next',
    back: 'Back',
    calculate: 'Calculate price',
    steps: [
      'Website type',
      'Extra pages',
      'Design',
      'Features',
      'Hosting',
      'Timeline'
    ],
    selectFeatures: 'Select extra features (optional)',
    includedFree: 'Included free',
    summary: 'Your quote',
    oneTimePrice: 'One-time costs',
    yearlyHosting: 'Hosting (per year)',
    totalFirstYear: 'Total first year',
    requestQuote: 'Request quote',
    name: 'Name',
    email: 'Email address',
    phone: 'Phone number',
    company: 'Company name',
    message: 'Additional notes',
    sending: 'Sending...',
    success: 'Thank you! We will contact you within 24 hours.',
    error: 'Something went wrong. Please try again.',
    close: 'Close',
    startOver: 'Start over',
    monthly: 'per month',
    yearly: 'per year',
    surcharge: 'rush fee',
    quotesRequested: 'quotes requested'
  },
  fr: {
    title: 'Calculateur de devis',
    subtitle: 'Calculez votre prix en 6 étapes',
    step: 'Étape',
    of: 'sur',
    next: 'Suivant',
    back: 'Retour',
    calculate: 'Calculer le prix',
    steps: ['Type de site', 'Pages supplémentaires', 'Design', 'Fonctionnalités', 'Hébergement', 'Délai'],
    selectFeatures: 'Sélectionnez des fonctionnalités (optionnel)',
    includedFree: 'Inclus gratuitement',
    summary: 'Votre devis',
    oneTimePrice: 'Coûts uniques',
    yearlyHosting: 'Hébergement (par an)',
    totalFirstYear: 'Total première année',
    requestQuote: 'Demander un devis',
    name: 'Nom',
    email: 'Adresse e-mail',
    phone: 'Téléphone',
    company: 'Entreprise',
    message: 'Remarques',
    sending: 'Envoi...',
    success: 'Merci! Nous vous contacterons dans les 24 heures.',
    error: 'Une erreur s\'est produite. Veuillez réessayer.',
    close: 'Fermer',
    startOver: 'Recommencer',
    monthly: 'par mois',
    yearly: 'par an',
    surcharge: 'frais urgence',
    quotesRequested: 'devis demandés'
  },
  de: {
    title: 'Angebots-Rechner',
    subtitle: 'Berechnen Sie Ihren Preis in 6 Schritten',
    step: 'Schritt',
    of: 'von',
    next: 'Weiter',
    back: 'Zurück',
    calculate: 'Preis berechnen',
    steps: ['Website-Typ', 'Extra Seiten', 'Design', 'Funktionen', 'Hosting', 'Zeitplan'],
    selectFeatures: 'Wählen Sie zusätzliche Funktionen (optional)',
    includedFree: 'Kostenlos enthalten',
    summary: 'Ihr Angebot',
    oneTimePrice: 'Einmalige Kosten',
    yearlyHosting: 'Hosting (pro Jahr)',
    totalFirstYear: 'Gesamt erstes Jahr',
    requestQuote: 'Angebot anfordern',
    name: 'Name',
    email: 'E-Mail-Adresse',
    phone: 'Telefon',
    company: 'Firma',
    message: 'Bemerkungen',
    sending: 'Wird gesendet...',
    success: 'Vielen Dank! Wir melden uns innerhalb von 24 Stunden.',
    error: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.',
    close: 'Schließen',
    startOver: 'Neu starten',
    monthly: 'pro Monat',
    yearly: 'pro Jahr',
    surcharge: 'Eilzuschlag',
    quotesRequested: 'Angebote angefordert'
  }
};

export const QuoteBuilder = ({ onClose, onComplete }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.nl;
  
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    websiteType: null,
    extraPages: 'none',
    design: 'template',
    features: [],
    hosting: 'basic',
    hostingPeriod: 'yearly',
    timeline: 'normal'
  });
  const [showSummary, setShowSummary] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = 6;

  const handleSelect = (key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
  };

  const handleFeatureToggle = (featureId) => {
    setSelections(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selections.websiteType !== null;
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handleBack = () => {
    if (showForm) {
      setShowForm(false);
    } else if (showSummary) {
      setShowSummary(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);

    const prices = calculateQuotePrice(selections);
    
    try {
      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          selections,
          prices,
          language
        })
      });

      if (!response.ok) throw new Error('Failed to submit');
      
      setSent(true);
      if (onComplete) onComplete(prices);
    } catch (err) {
      setError(t.error);
    } finally {
      setSending(false);
    }
  };

  const prices = calculateQuotePrice(selections);

  // Render step content
  const renderStep = () => {
    switch (currentStep) {
      case 0: // Website Type
        return (
          <div className="space-y-3">
            {quoteConfig.websiteTypes.map((type) => {
              const Icon = icons[type.icon] || FileText;
              const isSelected = selections.websiteType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => handleSelect('websiteType', type.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-[#2563EB] bg-[#2563EB]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">
                        {getQuoteText(type.name, language)}
                      </div>
                      <div className="text-sm text-slate-500">
                        {getQuoteText(type.description, language)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#2563EB]">€{type.basePrice}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 1: // Extra Pages
        return (
          <div className="space-y-3">
            {quoteConfig.extraPages.map((option) => {
              const isSelected = selections.extraPages === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect('extraPages', option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                    isSelected 
                      ? 'border-[#2563EB] bg-[#2563EB]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="font-medium text-slate-900">
                    {getQuoteText(option.name, language)}
                  </span>
                  <span className={`font-bold ${option.price > 0 ? 'text-[#2563EB]' : 'text-green-600'}`}>
                    {option.price > 0 ? `+€${option.price}` : '€0'}
                  </span>
                </button>
              );
            })}
          </div>
        );

      case 2: // Design
        return (
          <div className="space-y-3">
            {quoteConfig.designOptions.map((option) => {
              const isSelected = selections.design === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect('design', option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-[#2563EB] bg-[#2563EB]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">
                        {getQuoteText(option.name, language)}
                      </div>
                      <div className="text-sm text-slate-500">
                        {getQuoteText(option.description, language)}
                      </div>
                    </div>
                    <span className={`font-bold ${option.price > 0 ? 'text-[#2563EB]' : 'text-green-600'}`}>
                      {option.price > 0 ? `+€${option.price}` : '€0'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 3: // Features
        return (
          <div className="space-y-2">
            <p className="text-sm text-slate-500 mb-3">{t.selectFeatures}</p>
            {quoteConfig.features.map((feature) => {
              const isSelected = selections.features.includes(feature.id) || feature.included;
              return (
                <button
                  key={feature.id}
                  onClick={() => !feature.included && handleFeatureToggle(feature.id)}
                  disabled={feature.included}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                    feature.included
                      ? 'border-green-200 bg-green-50 cursor-default'
                      : isSelected 
                        ? 'border-[#2563EB] bg-[#2563EB]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${
                      isSelected ? 'bg-[#2563EB] text-white' : 'bg-slate-200'
                    } ${feature.included ? 'bg-green-500' : ''}`}>
                      {isSelected && <Check size={14} />}
                    </div>
                    <span className="text-sm font-medium text-slate-900">
                      {getQuoteText(feature.name, language)}
                    </span>
                  </div>
                  <span className={`text-sm font-bold ${
                    feature.included ? 'text-green-600' : feature.price > 0 ? 'text-[#2563EB]' : 'text-green-600'
                  }`}>
                    {feature.included ? t.includedFree : feature.price > 0 ? `+€${feature.price}` : '€0'}
                  </span>
                </button>
              );
            })}
          </div>
        );

      case 4: // Hosting
        return (
          <div className="space-y-3">
            {quoteConfig.hosting.map((option) => {
              const isSelected = selections.hosting === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect('hosting', option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-[#2563EB] bg-[#2563EB]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">
                        {getQuoteText(option.name, language)}
                      </div>
                      {option.description && (
                        <div className="text-sm text-slate-500">
                          {getQuoteText(option.description, language)}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {option.priceMonthly > 0 ? (
                        <>
                          <div className="font-bold text-[#2563EB]">€{option.priceMonthly}/{t.monthly.split(' ')[1]}</div>
                          <div className="text-xs text-slate-500">€{option.priceYearly}/{t.yearly.split(' ')[1]}</div>
                        </>
                      ) : (
                        <span className="font-bold text-green-600">€0</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 5: // Timeline
        return (
          <div className="space-y-3">
            {quoteConfig.timeline.map((option) => {
              const isSelected = selections.timeline === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect('timeline', option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-[#2563EB] bg-[#2563EB]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">
                      {getQuoteText(option.name, language)}
                    </span>
                    {option.multiplier > 1 ? (
                      <span className="text-sm font-bold text-orange-600">
                        {getQuoteText(option.surchargeLabel, language)}
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-green-600">✓</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  // Summary view with animations
  if (showSummary && !showForm && !sent) {
    const selectedType = quoteConfig.websiteTypes.find(t => t.id === selections.websiteType);
    const selectedPages = quoteConfig.extraPages.find(p => p.id === selections.extraPages);
    const selectedDesign = quoteConfig.designOptions.find(d => d.id === selections.design);
    const selectedHosting = quoteConfig.hosting.find(h => h.id === selections.hosting);
    const selectedTimeline = quoteConfig.timeline.find(t => t.id === selections.timeline);

    return (
      <motion.div 
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#60A5FA] flex items-center justify-center shadow-lg shadow-[#2563EB]/20">
            <Calculator size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t.summary}</h3>
            <p className="text-xs text-slate-500">Jouw configuratie</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 text-sm max-h-[200px] overflow-y-auto">
          <motion.div 
            className="flex justify-between items-center"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span className="font-medium text-slate-700">{getQuoteText(selectedType?.name, language)}</span>
            <span className="font-bold text-[#2563EB] bg-[#2563EB]/10 px-2 py-0.5 rounded-lg">€{selectedType?.basePrice}</span>
          </motion.div>
          {selectedPages?.price > 0 && (
            <motion.div 
              className="flex justify-between text-slate-600"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <span>{getQuoteText(selectedPages?.name, language)}</span>
              <span className="text-[#2563EB]">+€{selectedPages?.price}</span>
            </motion.div>
          )}
          {selectedDesign?.price > 0 && (
            <motion.div 
              className="flex justify-between text-slate-600"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span>{getQuoteText(selectedDesign?.name, language)}</span>
              <span className="text-[#2563EB]">+€{selectedDesign?.price}</span>
            </motion.div>
          )}
          {selections.features.filter(f => {
            const feat = quoteConfig.features.find(ft => ft.id === f);
            return feat && !feat.included && feat.price > 0;
          }).map((featureId, index) => {
            const feature = quoteConfig.features.find(f => f.id === featureId);
            return (
              <motion.div 
                key={featureId} 
                className="flex justify-between text-slate-600"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + index * 0.05 }}
              >
                <span className="flex items-center gap-1">
                  <Check size={12} className="text-green-500" />
                  {getQuoteText(feature?.name, language)}
                </span>
                <span className="text-[#2563EB]">+€{feature?.price}</span>
              </motion.div>
            );
          })}
          {selectedTimeline?.multiplier > 1 && (
            <motion.div 
              className="flex justify-between text-orange-600 bg-orange-50 -mx-4 px-4 py-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="flex items-center gap-1">
                <Rocket size={14} />
                {t.surcharge}
              </span>
              <span className="font-medium">+{Math.round((selectedTimeline.multiplier - 1) * 100)}%</span>
            </motion.div>
          )}
        </div>

        <motion.div 
          className="border-t border-slate-200 pt-4 space-y-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <div className="flex justify-between text-base">
            <span className="font-medium text-slate-700">{t.oneTimePrice}</span>
            <span className="font-bold text-[#2563EB]">€{prices.oneTime.toLocaleString('nl-NL')}</span>
          </div>
          {prices.hostingYearly > 0 && (
            <div className="flex justify-between text-slate-600 text-sm">
              <span>{t.yearlyHosting}</span>
              <span>€{prices.hostingYearly.toLocaleString('nl-NL')}</span>
            </div>
          )}
          <div className="flex justify-between text-xl font-bold border-t border-slate-200 pt-3 mt-2">
            <span className="text-slate-900">{t.totalFirstYear}</span>
            <span className="text-[#2563EB] bg-[#2563EB]/10 px-3 py-1 rounded-xl">€{prices.total.toLocaleString('nl-NL')}</span>
          </div>
        </motion.div>

        {/* 50% Deposit Notice in Summary */}
        <motion.div 
          className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38 }}
        >
          <span className="text-amber-500 text-lg flex-shrink-0">💡</span>
          <p className="text-xs text-amber-800">
            <strong>50% voorschot</strong> wordt gevraagd bij aanvang van het project. Het resterende bedrag wordt gefactureerd bij oplevering.
          </p>
        </motion.div>

        <motion.div 
          className="flex gap-2 pt-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBack}
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            {t.back}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowForm(true)}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white rounded-xl font-medium hover:from-[#1D4ED8] hover:to-[#2563EB] flex items-center justify-center gap-2 shadow-lg shadow-[#2563EB]/20 transition-all"
          >
            {t.requestQuote}
            <ArrowRight size={18} />
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  // Contact form with improved styling
  if (showForm && !sent) {
    return (
      <motion.form 
        onSubmit={handleSubmit} 
        className="space-y-4"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#60A5FA] flex items-center justify-center shadow-lg shadow-[#2563EB]/20">
            <Send size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t.requestQuote}</h3>
            <p className="text-xs text-slate-500">Vul je gegevens in</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-[#2563EB]/10 to-[#2563EB]/5 rounded-xl p-4 text-center border border-[#2563EB]/10">
          <span className="text-sm text-slate-600 block mb-1">{t.totalFirstYear}:</span>
          <span className="text-2xl font-bold text-[#2563EB]">€{prices.total.toLocaleString('nl-NL')}</span>
        </div>

        {/* 50% Deposit Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
          <span className="text-amber-500 text-lg">💡</span>
          <p className="text-xs text-amber-800">
            <strong>50% voorschot</strong> wordt gevraagd bij aanvang van het project. Het resterende bedrag wordt gefactureerd bij oplevering.
          </p>
        </div>

        {error && (
          <motion.div 
            className="bg-red-50 text-red-700 p-3 rounded-xl text-sm flex items-center gap-2 border border-red-100"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center text-xs">!</span>
            {error}
          </motion.div>
        )}

        <div className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t.name + ' *'}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all outline-none"
            />
          </div>
          <div className="relative">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder={t.email + ' *'}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder={t.phone}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all outline-none"
            />
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              placeholder={t.company}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all outline-none"
            />
          </div>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            placeholder={t.message}
            rows={2}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all outline-none resize-none"
          />
        </div>

        <div className="flex gap-2">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBack}
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            {t.back}
          </motion.button>
          <motion.button
            type="submit"
            disabled={sending}
            whileHover={{ scale: sending ? 1 : 1.02 }}
            whileTap={{ scale: sending ? 1 : 0.98 }}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white rounded-xl font-medium hover:from-[#1D4ED8] hover:to-[#2563EB] disabled:opacity-70 flex items-center justify-center gap-2 shadow-lg shadow-[#2563EB]/20 transition-all"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t.sending}
              </>
            ) : (
              <>
                <Send size={18} />
                {t.requestQuote}
              </>
            )}
          </motion.button>
        </div>
      </motion.form>
    );
  }

  // Success state with confetti
  if (sent) {
    return (
      <motion.div 
        className="text-center py-6 relative"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <Confetti />
        
        <motion.div 
          className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-200"
          initial={{ scale: 0 }}
          animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <CheckCircle className="w-10 h-10 text-white" />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            {t.success.split('!')[0]}!
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          </h3>
          <p className="text-slate-600 mb-4">{t.success.split('!')[1]}</p>
        </motion.div>
        
        <motion.div 
          className="bg-gradient-to-r from-[#2563EB]/10 to-[#2563EB]/5 rounded-xl p-5 mb-4 border border-[#2563EB]/10"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <span className="text-sm text-slate-600 block mb-1">{t.totalFirstYear}:</span>
          <span className="text-3xl font-bold text-[#2563EB]">€{prices.total.toLocaleString('nl-NL')}</span>
        </motion.div>

        {/* 50% Deposit Amount */}
        <motion.div 
          className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border border-amber-200"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">💡</span>
            <span className="text-sm font-semibold text-amber-800">50% voorschot bij aanvang:</span>
          </div>
          <span className="text-2xl font-bold text-amber-700">€{Math.round(prices.total * 0.5).toLocaleString('nl-NL')}</span>
          <p className="text-xs text-amber-600 mt-1">Restbedrag €{Math.round(prices.total * 0.5).toLocaleString('nl-NL')} bij oplevering</p>
        </motion.div>
        
        <motion.div 
          className="flex gap-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setSent(false);
              setShowForm(false);
              setShowSummary(false);
              setCurrentStep(0);
              setSelections({
                websiteType: null,
                extraPages: 'none',
                design: 'template',
                features: [],
                hosting: 'basic',
                hostingPeriod: 'yearly',
                timeline: 'normal'
              });
            }}
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            {t.startOver}
          </motion.button>
          {onClose && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white rounded-xl font-medium hover:from-[#1D4ED8] hover:to-[#2563EB] shadow-lg shadow-[#2563EB]/20 transition-all"
            >
              {t.close}
            </motion.button>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Step view
  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center">
            {currentStep + 1}
          </span>
          <span>{t.of} {totalSteps}</span>
        </span>
        <span className="font-medium text-[#2563EB] flex items-center gap-1">
          <Sparkles size={14} />
          {t.steps[currentStep]}
        </span>
      </div>
      
      {/* Progress bar with step indicators */}
      <div className="relative">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#2563EB] to-[#60A5FA]"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
        {/* Step dots */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-0">
          {[...Array(totalSteps)].map((_, i) => (
            <motion.div
              key={i}
              initial={false}
              animate={{
                scale: i <= currentStep ? 1 : 0.8,
                backgroundColor: i <= currentStep ? '#2563EB' : '#e2e8f0'
              }}
              className="w-2 h-2 rounded-full"
            />
          ))}
        </div>
      </div>

      {/* Live price with animation */}
      <motion.div 
        className="bg-gradient-to-r from-[#2563EB]/10 to-[#2563EB]/5 rounded-xl p-4 flex items-center justify-between border border-[#2563EB]/10"
        animate={{ scale: [1, 1.01, 1] }}
        transition={{ duration: 0.3 }}
        key={prices.oneTime}
      >
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-[#2563EB]" />
          <span className="text-sm font-medium text-slate-700">{t.oneTimePrice}:</span>
        </div>
        <motion.span 
          className="text-xl font-bold text-[#2563EB]"
          key={prices.oneTime}
          initial={{ scale: 1.2, color: '#10b981' }}
          animate={{ scale: 1, color: '#2563EB' }}
          transition={{ duration: 0.3 }}
        >
          <AnimatedPrice value={prices.oneTime} />
        </motion.span>
      </motion.div>

      {/* Step content with animation */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="max-h-[280px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent"
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex gap-2 pt-2">
        {currentStep > 0 && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBack}
            className="px-4 py-3 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center gap-1"
          >
            <ChevronLeft size={18} />
            {t.back}
          </motion.button>
        )}
        <motion.button
          whileHover={{ scale: canProceed() ? 1.02 : 1 }}
          whileTap={{ scale: canProceed() ? 0.98 : 1 }}
          onClick={handleNext}
          disabled={!canProceed()}
          className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white rounded-xl font-medium hover:from-[#1D4ED8] hover:to-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#2563EB]/20 transition-all"
        >
          {currentStep === totalSteps - 1 ? (
            <>
              <Calculator size={18} />
              {t.calculate}
            </>
          ) : (
            <>
              {t.next}
              <ChevronRight size={18} />
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default QuoteBuilder;
